//@flow
import * as React from 'react';
import { withRouter } from 'common/components/core/router';
import { connect } from 'react-redux';
import compose from 'recompose/compose';
import { clientSelector } from 'common/redux/selectors/queryParamsSelectors';
import { translate } from 'react-i18next';
import { Dialog } from 'common/components/core/dialog';
import {
    RadioGroup,
    RadioButton
} from 'common/components/core/form/RadioButton';
import { ButtonTypes } from 'common/components/core/button';
import { dashboardColors } from 'common/enums/ColorEnum';
import { FilterBuilder, FilterOperation } from 'common/models/internal/Filter';
import FilterListBuilder from 'common/models/helpers/FilterListBuilder';
import { getSelectedBUId } from 'common/redux/selectors/appPreferences';
import ExportEmailComponent from 'common/entity/generic/ExportEmailComponent';
import { personnelSelector } from 'common/redux/selectors/sessionSelector';
import { GlobalTimezoneMessage } from 'common/modules/timezone';
import { Stack } from '@locus-taxy/neo-ui';

import theme from 'common/entity/generic/MasterExportContainer.scss';

type Props = {
    clientId: string,
    businessUnitId: string,
    onDismiss: Function,
    onAccept: Function,
    columns: Array<Object>,
    selectedRows: Array<string>,
    filteredEntriesCount: number,
    filteringEnabled: boolean,
    appliedFilters: FilterListBuilder,
    entityNameId: string,
    exportErrorMessage: ?string,
    hideExportOptions: boolean,
    t: Function,
    getExtraFiltersForFetchData: Function,
    defaultEmail: string,
    getAdditionalColumns: ?Function,
    showLoader?: boolean,
    shouldExportInactiveEntities?: boolean,
    useGeneralExportTypes?: boolean
};

type State = {
    selectedOption: ?string
};

const mapStateToProps = (state, props) => {
    const clientId = clientSelector(state, props);
    const businessUnitId = getSelectedBUId(state, props);
    const personnel = personnelSelector(state);

    return {
        clientId,
        businessUnitId,
        defaultEmail: personnel?.email
    };
};
const ROW_EXPORT_TYPES = {
    SELECTED_ROWS: 'selectedRows',
    FILTERED_ROWS: 'filteredRows',
    ALL_ROWS: 'allRows'
};
const GENERAL_EXPORT_TYPES = {
    SELECTED: 'selected',
    FILTERED: 'filtered',
    ALL: 'all'
};
class MasterExportContainer extends React.PureComponent<Props, State> {
    constructor(props) {
        super(props);
        const { selectedRows, filteringEnabled } = props;
        let selectedOption = selectedRows.length
            ? ROW_EXPORT_TYPES.SELECTED_ROWS
            : filteringEnabled
            ? ROW_EXPORT_TYPES.FILTERED_ROWS
            : ROW_EXPORT_TYPES.ALL_ROWS;
        if (props.useGeneralExportTypes) {
            selectedOption = selectedRows.length
                ? GENERAL_EXPORT_TYPES.SELECTED
                : filteringEnabled
                ? GENERAL_EXPORT_TYPES.FILTERED
                : GENERAL_EXPORT_TYPES.ALL;
        }
        this.state = {
            selectedOption
        };
    }

    buildExportRequestParams = () => {
        const {
            columns,
            selectedRows,
            appliedFilters,
            entityNameId,
            businessUnitId,
            getExtraFiltersForFetchData,
            getAdditionalColumns,
            shouldExportInactiveEntities
        } = this.props;
        const { selectedOption } = this.state;
        const { defaultEmail: emailAddress } = this.props;
        const accessors = new Set();
        let columnAccessors = [];

        columns.forEach(column => {
            if (column.show) {
                if (column.exportId) {
                    accessors.add(column.exportId);
                } else if (typeof column.accessor === 'string') {
                    accessors.add(column.accessor);
                } else {
                    const value = column.fieldId || column.id;
                    accessors.add(value);
                }
            }
        });

        columnAccessors = getAdditionalColumns
            ? getAdditionalColumns(Array.from(accessors))
            : Array.from(accessors);

        // Default Master Filter
        const defaultActiveFilter = shouldExportInactiveEntities
            ? []
            : new FilterListBuilder()
                  .add(
                      new FilterBuilder()
                          .withName('status')
                          .withOperation(FilterOperation.Equals)
                          .withValue('ACTIVE')
                  )
                  .build();

        let baseFilter = [];
        if (getExtraFiltersForFetchData) {
            const filters = getExtraFiltersForFetchData([]);
            baseFilter = new FilterListBuilder().addAll(filters).build();
        }

        const requestParams = {
            businessUnitId,
            selectedColumns: columnAccessors,
            emailAddresses: [{ emailAddress }],
            entityName: entityNameId,
            filters: [...defaultActiveFilter, ...baseFilter],
            selectedIds: []
        };

        const filters = appliedFilters && appliedFilters.build();
        const updatedFilters = [...filters, ...defaultActiveFilter];

        switch (selectedOption) {
            case ROW_EXPORT_TYPES.SELECTED_ROWS:
            case GENERAL_EXPORT_TYPES.SELECTED: {
                requestParams.filters = null;
                requestParams.selectedIds = selectedRows;
                break;
            }
            case ROW_EXPORT_TYPES.FILTERED_ROWS:
            case GENERAL_EXPORT_TYPES.FILTERED: {
                requestParams.filters = updatedFilters;
                requestParams.selectedIds = [];
                break;
            }
        }
        return requestParams;
    };

    onAccept = () => {
        const { onAccept } = this.props;
        this.setState({
            showLoader: true
        });
        const requestParams = this.buildExportRequestParams();
        onAccept(requestParams);
    };

    onRadioGroupChange = type => {
        this.setState({
            selectedOption: type
        });
    };

    renderExportTypes = () => {
        const {
            filteringEnabled,
            filteredEntriesCount,
            selectedRows,
            useGeneralExportTypes,
            t
        } = this.props;
        const exportTypes = useGeneralExportTypes
            ? GENERAL_EXPORT_TYPES
            : ROW_EXPORT_TYPES;
        return Object.values(exportTypes).map(type => {
            const value =
                (type === ROW_EXPORT_TYPES.SELECTED_ROWS ||
                    type === GENERAL_EXPORT_TYPES.SELECTED) &&
                selectedRows.length
                    ? `(${selectedRows.length})`
                    : (type === ROW_EXPORT_TYPES.FILTERED_ROWS ||
                          type === GENERAL_EXPORT_TYPES.FILTERED) &&
                      filteringEnabled
                    ? `(${filteredEntriesCount})`
                    : null;
            const label = (
                <div className={theme.radioLabel}>
                    {t(`entityExport.${type}`)}
                    &nbsp;
                    {value}
                </div>
            );
            return (
                <RadioButton
                    data-test-id={`${type}-export-button`}
                    key={`${type}`}
                    theme={theme}
                    label={label}
                    value={type}
                    disabled={
                        (!filteringEnabled &&
                            (type === ROW_EXPORT_TYPES.FILTERED_ROWS ||
                                type === GENERAL_EXPORT_TYPES.FILTERED)) ||
                        ((type === ROW_EXPORT_TYPES.SELECTED_ROWS ||
                            type === GENERAL_EXPORT_TYPES.SELECTED) &&
                            !selectedRows.length)
                    }
                />
            );
        });
    };

    renderExportOptions = () => {
        const { t } = this.props;
        const { selectedOption } = this.state;

        return (
            <>
                <div className={theme.dialogSubtitle}>
                    {t('entityExport.export')}
                </div>
                <RadioGroup
                    onChange={this.onRadioGroupChange}
                    value={selectedOption}
                    theme={theme}
                >
                    {this.renderExportTypes()}
                </RadioGroup>
            </>
        );
    };

    onDialogDismiss = () => {
        const { onDismiss } = this.props;
        onDismiss && onDismiss();
    };

    render() {
        const {
            exportErrorMessage,
            t,
            entityNameId,
            hideExportOptions,
            hideSubtitle,
            showLoader
        } = this.props;
        const { selectedOption } = this.state;
        const { defaultEmail: emailAddress } = this.props;
        return (
            <Dialog
                active
                theme={theme}
                className={theme.entityExportContainer}
                acceptActionLabel={t('entityExport.export')}
                title={t('entityExport.exportTitle', {
                    entityNameId
                })}
                isLoading={showLoader}
                subtitle={!hideSubtitle ? t('entityExport.exportSubtitle') : ''}
                headerColor={dashboardColors.duskBlue}
                onDismiss={this.onDialogDismiss}
                onAccept={this.onAccept}
                disableAccept={!selectedOption}
                acceptButtonType={ButtonTypes.Text}
                acceptActionColor={dashboardColors.coolGreen.name}
                acceptErrorMessage={exportErrorMessage}
            >
                <Stack space="medium">
                    {!hideExportOptions ? this.renderExportOptions() : null}
                    <ExportEmailComponent email={emailAddress} disabled />
                    <GlobalTimezoneMessage />
                </Stack>
            </Dialog>
        );
    }
}

export default compose(
    withRouter,
    connect(mapStateToProps),
    translate('entityManagement')
)(MasterExportContainer);
